import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { formatPt } from '../../../lib/format.js';
import { RequirementBreakdownCharts } from './RequirementBreakdownCharts.js';
import { DevelopPointViewSwitch } from './DevelopPointViewSwitch.js';
import {
  DEFAULT_DEVELOP_POINT_VIEW,
  supportsRequirementView,
  type DevelopPointView,
} from './develop-point-view.js';
import type { KpiTeam, KpiTrendPoint, KpiTrendWip } from '../../../types/api.js';

/**
 * 開発pt チャートのツールチップ明細の表示順（上から）。
 * Recharts の Tooltip は Line 宣言順では並ばないため itemSorter で明示制御する。
 * 目標（予算） → 目標（キャパ） → 実績
 * （要件定義抜きビューでは 目標（キャパ）線を描かないので 目標（予算） → 実績 になる）
 * @graph-connects none
 */
const DEVELOP_TOOLTIP_ORDER: Record<string, number> = {
  target: 0,
  capacityTarget: 1,
  value: 2,
};

interface DevelopPointMtdCardProps {
  /** KPIトレンドの日次ポイント列 */
  points: KpiTrendPoint[];
  /** トレンドチャート読み込み状態 */
  isLoading: boolean;
  /** チャート説明文 */
  description: string;
  /** チャートクリック時の詳細モーダル表示コールバック */
  onOpenDetail: (state: { activeLabel?: string | number }) => void;
  /** X 軸の目盛ラベル整形 */
  tickFormatter: (value: string) => string;
  /** ツールチップのラベル整形 */
  labelFormatter: (label: string) => string;
  /** ツールチップの見た目 */
  tooltipStyle: Record<string, string>;
  /** 上部の team フィルタ。要件定義ビューの表示可否判定に使う */
  team?: KpiTeam | 'all';
  /**
   * 選択中のビュー（親が週次pt集計カードと共有する場合に渡す）。
   * 未指定ならカード内で状態を持つ（既定は要件定義抜き）
   */
  view?: DevelopPointView;
  /** ビュー変更コールバック（view と組で渡す） */
  onViewChange?: (view: DevelopPointView) => void;
  /**
   * 現時点の処理中pt（要件定義 / それ以外）。内訳ビューで完了実績の上に破線枠で積む。
   * 日次の `points` ではなく API レスポンス直下から渡す（処理中pt は MTD 累積ではなく
   * 時点の在庫で、工程別の履歴が無いため折れ線には乗せない）。
   */
  wip?: KpiTrendWip;
}

/**
 * 「開発pt (MTD)」カード。
 *
 * 全体 / PDG 系スコープでは右上のスイッチで 3 ビューを切り替えられる:
 *  - 全部: 目標(予算) / 目標(キャパ) / 実績（従来の表示）
 *  - 要件定義抜き: 目標(予算) を `_PDG_v2` の「開発pt（要件定義を除く）」に、実績を
 *    要件定義セット子票を除いた値に差し替える。目標(キャパ) 線は描かない（キャパは要件定義を
 *    含んだ総量で、要件定義を除いた予算 / 実績と同じ軸に並べると過達に見えるため）
 *  - 内訳: 実績(完了) と 処理中pt の構成比（円）と、現時点進捗目標の予算 vs 実績(完了 + 処理中)（棒）。
 *    完了は実塗り・処理中は破線枠で、要件定義のように完了まで時間がかかる工程が「着手もして
 *    いない」と読まれないようにする
 *
 * ACE 単独 / ACE サブチームではスイッチを出さず「全部」相当の表示に固定する（要件定義工程が
 * PDG 側にしか無く、ACE の予算シートに対応する行が無い）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPI ダッシュボードの開発pt (MTD) カード。全部 / 要件定義抜き / 内訳 の 3 ビューを右上のスイッチで切り替え（全体 + PDG 系スコープのみ）、要件定義を開発ptと別管理にした fibo予算 `_PDG_v2` の目標線と、要件定義セット子票を除いた実績線を描画する。要件定義抜きビューは目標(キャパ)線を出さず、内訳ビューの棒グラフは現時点進捗目標（日割り累積）の予算と 完了(実塗り) + 処理中(破線枠) の積み棒を使う。折れ線（全部 / 要件定義抜き）には処理中pt を乗せない（MTD 累積の完了口径のまま）
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects DevelopPointViewSwitch [calls] ビュー切替スイッチ描画（週次pt集計カードと状態共有）
 * @graph-connects ComposedChart [calls] 開発pt トレンドチャート描画
 * @graph-connects RequirementBreakdownCharts [calls] 内訳ビューの円 / 棒グラフ描画
 * @graph-connects supportsRequirementView [calls] team スコープがビュー切替対象かを判定
 */
export function DevelopPointMtdCard({
  points,
  isLoading,
  description,
  onOpenDetail,
  tickFormatter,
  labelFormatter,
  tooltipStyle,
  team,
  view: controlledView,
  onViewChange,
  wip,
}: DevelopPointMtdCardProps) {
  const { t } = useTranslation();
  const [localView, setLocalView] = useState<DevelopPointView>(DEFAULT_DEVELOP_POINT_VIEW);
  const view = controlledView ?? localView;
  const setView = onViewChange ?? setLocalView;

  const canSwitchView = supportsRequirementView(team);
  // team を切り替えて要件定義ビュー非対応スコープへ移った場合も「全部」として描画する
  // （state を戻さないのは、PDG に戻ったときに選んでいたビューを保つため）
  const effectiveView: DevelopPointView = canSwitchView ? view : 'all';
  const excludeRequirement = effectiveView === 'excludeRequirement';

  const chartData = useMemo(
    () =>
      points.map((point) => ({
        date: point.date,
        value: excludeRequirement
          ? point.developPointMtdExcludingRequirement
          : point.developPointMtd,
        target: excludeRequirement
          ? point.developPointMtdTargetExcludingRequirement
          : point.developPointMtdTarget,
        // 要件定義抜きビューでは目標(キャパ)を出さない（要件定義を含む総量なので、
        // 要件定義を除いた予算 / 実績と同じ軸に並べると達成度を読み誤らせる）
        capacityTarget: excludeRequirement ? undefined : point.developPointMtdCapacityTarget,
      })),
    [points, excludeRequirement],
  );

  // 内訳ビューは当月の最新値（トレンド末尾）を使う。MTD 累積なので末尾が「今月ここまで」
  const latest = points.length > 0 ? points[points.length - 1] : undefined;

  const hasData = points.length > 0;

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.developPointMtd')}
        description={
          // 内訳ビューは当月の内訳しか出さないので、トレンド期間の説明文（「過去 N 日間の推移」）を
          // そのまま出すと期間を誤読させる。小見出しはパネル側が持つのでカード側は出さない
          effectiveView === 'breakdown'
            ? undefined
            : excludeRequirement
              ? t('kpi.developPointView.excludeRequirementDescription')
              : description
        }
        action={
          canSwitchView ? (
            <DevelopPointViewSwitch view={effectiveView} onChange={setView} />
          ) : undefined
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-48 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : !hasData ? (
          <div className="h-48 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        ) : effectiveView === 'breakdown' ? (
          <RequirementBreakdownCharts
            requirementActual={latest?.developPointMtdRequirement ?? null}
            developActual={latest?.developPointMtdExcludingRequirement ?? null}
            requirementTarget={latest?.developPointMtdRequirementTarget ?? null}
            developTarget={latest?.developPointMtdTargetExcludingRequirement ?? null}
            wip={wip}
          />
        ) : (
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                onClick={onOpenDetail as never}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="date"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={tickFormatter}
                />
                <YAxis
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => String(value)}
                />
                <Tooltip
                  position={{ x: 0, y: 0 }}
                  wrapperStyle={{
                    top: 0,
                    right: 20,
                    left: 'auto',
                    transform: 'translateY(-100%)',
                    zIndex: 50,
                  }}
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  itemSorter={
                    ((item: { name?: string }) =>
                      DEVELOP_TOOLTIP_ORDER[item.name ?? ''] ?? 99) as never
                  }
                  formatter={
                    ((value: number, name: string) => {
                      if (name === 'target') {
                        return [
                          `${formatPt(value ?? 0)}pt`,
                          excludeRequirement
                            ? t('kpi.developPointMtdTargetExcludingRequirement')
                            : t('kpi.developPointMtdTarget'),
                        ];
                      }
                      if (name === 'capacityTarget') {
                        return [
                          `${formatPt(value ?? 0)}pt`,
                          t('kpi.developPointMtdCapacityTarget'),
                        ];
                      }
                      return [
                        `${formatPt(value ?? 0)}pt`,
                        excludeRequirement
                          ? t('kpi.developPointMtdExcludingRequirement')
                          : t('kpi.developPointMtd'),
                      ];
                    }) as never
                  }
                  labelFormatter={labelFormatter as never}
                />
                <Line
                  dataKey="target"
                  name="target"
                  stroke="var(--color-kpi-target-budget)"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="5 3"
                  connectNulls={false}
                />
                {!excludeRequirement && (
                  <Line
                    dataKey="capacityTarget"
                    name="capacityTarget"
                    stroke="#7dd3fc"
                    strokeWidth={1.5}
                    dot={false}
                    strokeDasharray="5 3"
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="value"
                  name="value"
                  stroke="var(--color-success)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: 'var(--color-success)' }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
